// Text that types itself in the way a model streams a reply: uneven chunks of LLM-like tokens, some of
// them partial words. Used by the home page chat box's examples and the chat's reassurance headlines.

/** A source of random numbers in [0, 1), as Math.random. */
type Rand = () => number;

const randInt = (min: number, max: number, rand: Rand = Math.random): number => min + Math.floor(rand() * (max - min + 1));

/**
 * Random numbers that are the same for the same text every time (mulberry32 seeded from the text), so a
 * page rendered on the server types in exactly the same chunks once the browser takes it over.
 */
export const seededRandom = (seed: string): Rand => {
  let state = 2166136261;
  for (let i = 0; i < seed.length; i++) state = Math.imul(state ^ seed.charCodeAt(i), 16777619);
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

/**
 * Splits text roughly the way an LLM tokenizer does, so the examples stream in like a model's reply:
 * short words whole (with their leading space), longer words in subword pieces ("daugh" + "ter"),
 * numbers in groups of up to three digits ("140" + "0"), punctuation on its own.
 */
export const llmTokens = (text: string, rand: Rand = Math.random): string[] => {
  const tokens: string[] = [];
  const re = /’[A-Za-z]+|\s?[A-Za-z]+|\s?[0-9]{1,3}|\s?[^\sA-Za-z0-9]+|\s+/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text))) {
    const piece = match[0];
    const lead = piece.startsWith(' ') ? 1 : 0;
    // Words up to five letters (most common words) are one token; longer ones split into pieces.
    if (!/[A-Za-z]{6,}/.test(piece)) {
      tokens.push(piece);
      continue;
    }
    let at = 0;
    while (at < piece.length) {
      const size = at === 0 ? lead + randInt(3, 5, rand) : randInt(2, 4, rand);
      // never leave a one-letter tail
      const end = piece.length - (at + size) < 2 ? piece.length : at + size;
      tokens.push(piece.slice(at, end));
      at = end;
    }
  }
  return tokens;
};

/** Where each streaming step ends: one to three tokens at a time, like chunks arriving from a model. */
export const typingSteps = (text: string, rand: Rand = Math.random): number[] => {
  const ends: number[] = [];
  let end = 0;
  const tokens = llmTokens(text, rand);
  for (let i = 0; i < tokens.length; ) {
    const take = Math.min(tokens.length - i, randInt(1, 3, rand));
    for (let j = 0; j < take; j++) end += tokens[i + j].length;
    i += take;
    ends.push(end);
  }
  return ends;
};

/** Gap before the next chunk: quick and uneven, with the odd short stall, as a model's stream is. */
export const streamDelay = (rand: Rand = Math.random): number => (rand() < 0.08 ? randInt(220, 380, rand) : randInt(40, 120, rand));

/**
 * Text as the chunks a model would stream it in, each with the time (ms) it arrives, and when the last one
 * has finished fading in. The same text always gives the same chunks and timings (seeded), so it can be
 * drawn on the server and played by CSS alone.
 */
export const streamedChunks = (text: string, { start = 120, fade = 220 } = {}): { chunks: Array<{ text: string; at: number }>; total: number } => {
  const rand = seededRandom(text);
  const chunks: Array<{ text: string; at: number }> = [];
  let from = 0;
  let at = start;
  for (const end of typingSteps(text, rand)) {
    chunks.push({ text: text.slice(from, end), at });
    from = end;
    at += streamDelay(rand);
  }
  const last = chunks[chunks.length - 1]?.at ?? 0;
  return { chunks, total: last + fade };
};
